/**
 * Persist the intended return path across the Google OAuth round-trip.
 *
 * RequireAuth bounces unauthenticated users to /login; the OAuth flow is a
 * full-page redirect (→ /api/auth/google → Google → /auth/callback), so router
 * location.state does NOT survive. sessionStorage does (same tab), and
 * AuthCallback consumes it after auth resolves.
 *
 * Closes a silent post-checkout gap: a paid top-up that lands on
 * /app?checkout=success with an expired session would otherwise have the flag
 * stripped at the auth gate and never surface the success banner.
 *
 * Trust boundary: only same-origin relative paths are accepted — protocol-
 * relative (//host) and absolute URLs are rejected to prevent open-redirect via
 * a tampered stored value.
 */

const RETURN_PATH_KEY = 'piccard.returnPath'

function isSafeRelativePath(path: string): boolean {
  // Must start with `/` (relative) and not `//` (protocol-relative → foreign host).
  return path.startsWith('/') && !path.startsWith('//')
}

/** Store the path to return to after login. Silently ignores unsafe inputs. */
export function stashReturnPath(path: string): void {
  if (typeof window === 'undefined' || !isSafeRelativePath(path)) return
  try {
    window.sessionStorage.setItem(RETURN_PATH_KEY, path)
  } catch {
    // sessionStorage unavailable (private mode / disabled) — swallow; worst
    // case is landing on /app instead of the deep link.
  }
}

/** Read and clear the stored return path. Returns null if absent or unsafe. */
export function consumeReturnPath(): string | null {
  if (typeof window === 'undefined') return null
  try {
    const next = window.sessionStorage.getItem(RETURN_PATH_KEY)
    window.sessionStorage.removeItem(RETURN_PATH_KEY)
    return next && isSafeRelativePath(next) ? next : null
  } catch {
    return null
  }
}
